<template>
  <div id="aml-report">
    <PageControl
      :title="'Anti-Money Laundering Report'"
      :elements_query="sheet_elements"
    >
      <button
        @click="show_export_modal"
        class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
      >
        <i class="bi bi-download"></i>
        Export Data
      </button>

      <!-- Export Modal -->
      <Teleport to="body">
        <div
          class="modal fade"
          :id="'export-modal-' + application_id"
          tabindex="-1"
          aria-labelledby="exportModalLabel"
          aria-hidden="true"
        >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content rounded-4 shadow">
            <div class="modal-header">
              <h6 class="modal-title fw-semibold text-muted">
                Export AML Report Data
              </h6>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label fw-bold"
                  >Select Tables to Export:</label
                >
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.subject_info"
                    id="subjectInfo"
                  />
                  <label class="form-check-label" for="subjectInfo">
                    Subject Information
                  </label>
                </div>
                <div class="form-check" v-if="nm_count > 0">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.negative_media"
                    id="negativeMedia"
                  />
                  <label class="form-check-label" for="negativeMedia">
                    Negative Media Results ({{ nm_count }} records)
                  </label>
                </div>
                <div class="form-check" v-if="pep_count > 0">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.pep_results"
                    id="pepResults"
                  />
                  <label class="form-check-label" for="pepResults">
                    PEP Results ({{ pep_count }} records)
                  </label>
                </div>
                <div class="form-check" v-if="wls_count > 0">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.watchlist"
                    id="watchlist"
                  />
                  <label class="form-check-label" for="watchlist">
                    Watchlist Results ({{ wls_count }} records)
                  </label>
                </div>
                <div class="form-check" v-if="export_format === 'json'">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.raw_json"
                    id="rawJson"
                  />
                  <label class="form-check-label" for="rawJson">
                    Include raw JSON data
                  </label>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-bold">Export Format:</label>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="csv"
                    id="csvFormat"
                  />
                  <label class="form-check-label" for="csvFormat">
                    CSV (Separate files for each table)
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="excel"
                    id="excelFormat"
                  />
                  <label class="form-check-label" for="excelFormat">
                    Excel (Multiple sheets in one file)
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="json"
                    id="jsonFormat"
                  />
                  <label class="form-check-label" for="jsonFormat">
                    JSON (Complete data export)
                  </label>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
                @click="close_modal"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-bg-green text-xs rounded-small shadow-sm"
                @click="export_data"
                :disabled="!has_selected_tables"
              >
                <i class="bi bi-download me-1"></i>
                Export Data
              </button>
            </div>
          </div>
        </div>
        </div>
      </Teleport>
    </PageControl>
    <PageComponent>
      <SectionSheet>
        <div
          class="container-fluid py-3 header d-flex align-items-center justify-content-between border-bottom mb-3"
        >
          <div class="d-flex">
            <img
              class="logo"
              width="295"
              height="125"
              src="@/assets/images/new_black_logo2.svg"
              alt="Logo"
            />
          </div>
          <div class="d-flex text-end">
            <div>
              <div class="h4 fw-bold mb-1">Anti-Money Laundering Report</div>
              <div class="text-muted text-xs">
                28 Fricker Rd,<br />
                Illovo, Johannesburg,<br />
                2196 South Africa<br />
                Website:
                <a
                  href="https://www.goodpeopledata.com/"
                  target="_blank"
                  class="text-decoration-none text-muted"
                  >www.goodpeopledata.com</a
                >
              </div>
            </div>
          </div>
        </div>

        <div
          class="card shadow-sm border bg-white mb-2"
          v-if="total_matches > 0"
        >
          <div class="card-header">
            <h6 class="mb-0">AML Screening Summary</h6>
          </div>
          <div class="card-body">
            <p>
              This report presents Anti-Money Laundering screening results for
              <span class="fw-bold text-dark">
                {{ subject_full_name }}
              </span>
              (Date of Birth: {{ subject_date_of_birth }}, Country:
              {{ subject_country }}). The comprehensive screening was conducted
              using three primary data sources:
            </p>
            <ul class="mt-1 mb-2">
              <li>Negative Media Database</li>
              <li>Politically Exposed Person (PEP) Database</li>
              <li>Global Watchlist Database</li>
            </ul>
            <div
              class="d-flex justify-content-center align-items-center m-0 p-2 bg-white"
            >
              <div class="provider-box text-center">
                <span class="fw-bold">
                  {{ total_matches }} potential match(es) found
                </span>
                <br />
                requiring further review and verification.
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="card bg-white mb-4 shadow-sm border p-2">
            <div class="card-header">
              <h6 class="mb-0">AML Screening Summary</h6>
            </div>
            <div class="card-body">
              <p class="mb-3">
                This <strong>Anti-Money Laundering Verification</strong> was
                performed for
                <strong>{{ subject_full_name || "the subject" }}</strong>
                <span v-if="subject_date_of_birth">
                  (Date of Birth: {{ subject_date_of_birth }})</span
                >
                <span v-if="subject_country"> from {{ subject_country }}</span
                >. The subject was thoroughly screened against multiple
                databases to ensure compliance.
              </p>

              <div class="text-center py-3">
                <div class="alert alert-success mb-0">
                  <i class="fas fa-check-circle fa-3x text-success mb-3"></i>
                  <h5 class="text-success">No Matches Found</h5>
                  <p class="mb-0">
                    No matches were found in any of the screened databases. All
                    checks completed successfully with clean results.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Results Summary Cards - Compact Formal Version -->
        <div class="row mb-2" v-if="total_matches > 0">
          <div class="col-12">
            <div class="card shadow-sm border mb-3">
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th class="ps-3">Category</th>
                        <th>Results</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="ps-3">
                          <div class="d-flex align-items-center">
                            <i class="fas fa-newspaper text-warning me-2"></i>
                            <span class="fw-bold">Negative Media</span>
                          </div>
                        </td>
                        <td
                          class="d-flex align-items-start justify-content-center"
                        >
                          <span class="badge bg-light text-dark border w-100">{{
                            nm_count
                          }}</span>
                        </td>
                        <td class="text-muted small">
                          Matches found in negative media databases indicating
                          potential adverse publicity.
                        </td>
                      </tr>
                      <tr>
                        <td class="ps-3">
                          <div class="d-flex align-items-center">
                            <i class="fas fa-user-tie text-info me-2"></i>
                            <span class="fw-bold"
                              >Politically Exposed Person</span
                            >
                          </div>
                        </td>
                        <td
                          class="d-flex align-items-start justify-content-center"
                        >
                          <span class="badge bg-light text-dark border w-100">{{
                            pep_count
                          }}</span>
                        </td>
                        <td class="text-muted small">
                          Matches found in PEP databases for individuals in
                          prominent political positions.
                        </td>
                      </tr>
                      <tr>
                        <td class="ps-3">
                          <div class="d-flex align-items-center">
                            <i
                              class="fas fa-exclamation-triangle text-danger me-2"
                            ></i>
                            <span class="fw-bold">Watchlist</span>
                          </div>
                        </td>
                        <td
                          class="d-flex align-items-start justify-content-center"
                        >
                          <span class="badge bg-light text-dark border w-100">{{
                            wls_count
                          }}</span>
                        </td>
                        <td class="text-muted small">
                          Matches found in global watchlist databases for
                          sanctions or regulatory concerns.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Subject Information -->
        <div class="card shadow-sm border bg-white p-2 mb-2">
          <div class="card-header">
            <h6 class="mb-0">Subject Information</h6>
          </div>
          <div class="card-body">
            <table class="info-table table table-sm">
              <tr>
                <td>Full Name</td>
                <td>{{ subject_full_name || "N/A" }}</td>
              </tr>
              <tr>
                <td>First Name</td>
                <td>{{ subject_first_name || "N/A" }}</td>
              </tr>
              <tr>
                <td>Last Name</td>
                <td>{{ subject_last_name || "N/A" }}</td>
              </tr>
              <tr>
                <td>Date of Birth</td>
                <td>{{ subject_date_of_birth || "N/A" }}</td>
              </tr>
              <tr>
                <td>Gender</td>
                <td>{{ subject_gender || "N/A" }}</td>
              </tr>
              <tr>
                <td>Country</td>
                <td>{{ subject_country || "N/A" }}</td>
              </tr>
              <tr>
                <td>Application ID</td>
                <td>{{ application_id || "N/A" }}</td>
              </tr>
            </table>
          </div>
        </div>

        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Score Date:</span> {{ score_date }}
          </div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>

      <!-- Negative Media Results -->
      <template v-if="nm_results && nm_results.length > 0">
        <template
          v-for="(chunk, chunk_index) in Math.ceil(
            limited_nm_results.length / 2
          )"
          :key="`nm-result-${chunk_index}`"
        >
          <SectionSheet>
            <!-- Section Header for first chunk -->
            <template v-if="chunk_index === 0">
              <div class="card shadow-sm border bg-white p-2 mb-3 nm-card">
                <div class="card-header">
                  <h6 class="mb-0">Negative Media Results</h6>
                </div>
                <div class="card-body">
                  <p class="text-muted mb-0">
                    The following results were found in negative media screening
                    databases.
                  </p>
                </div>
              </div>
            </template>
            <template
              v-for="(result, i) in limited_nm_results.slice(
                chunk_index === 0 ? 0 : chunk_index * 2 - 1,
                chunk_index === 0 ? 1 : chunk_index * 2 - 1 + 2
              )"
              :key="`nm-result-${
                chunk_index === 0 ? i : chunk_index * 2 - 1 + i
              }`"
            >
              <div class="card border border-left-accent nm-card mb-3">
                <div class="card-body p-3">
                  <div class="nm-result-card">
                    <div
                      class="d-flex justify-content-between align-items-start mb-2"
                    >
                      <div class="result-header">
                        <span
                          class="badge text-white"
                          style="background-color: #036016"
                          >Score: {{ result.score }}</span
                        >
                        <span class="badge bg-secondary ms-2"
                          >Category: {{ result.category }}</span
                        >
                        <span
                          v-if="result.concepts && result.concepts.length"
                          class="badge bg-light text-dark ms-2"
                        >
                          Concepts:
                          {{ result.concepts.join(", ") }}
                        </span>
                      </div>
                      <small class="text-muted"
                        >ID: {{ result.productId }}</small
                      >
                    </div>
                    <div class="rounded p-2 bg-light">
                      <div v-if="result.articleTitle" class="mb-2 pt-2">
                        <div class="d-flex align-items-center">
                          <i class="bi bi-newspaper me-2"></i>
                          <span
                            class="fw-bold text-dark h6 mb-0"
                            :title="result.articleTitle"
                          >
                            {{ result.articleTitle }}
                          </span>
                        </div>
                        <div
                          v-if="result.articleDate"
                          class="text-muted small ms-4"
                        >
                          Published:
                          {{
                            new Date(result.articleDate).toLocaleDateString()
                          }}
                        </div>
                      </div>

                      <div class="row mb-2">
                        <div class="col-md-4 mb-2 text-center">
                          <div class="text-muted small">Subject</div>
                          <div class="fw-bold text-dark">
                            {{ result.subjectMatched }}
                          </div>
                        </div>
                        <div class="col-md-4 mb-2 text-center">
                          <div class="text-muted small">Country</div>
                          <div class="fw-bold text-dark">
                            {{ result.sourceCountry }}
                          </div>
                        </div>
                        <div class="col-md-4 mb-2 text-center">
                          <div class="text-muted small">Source ID</div>
                          <div class="fw-bold text-dark">
                            {{ result.sourceId }}
                          </div>
                        </div>
                        <div class="col-md-4 mb-2 text-center">
                          <div class="text-muted small">Subject Age</div>
                          <div class="fw-bold text-dark">
                            {{ result.subjectAge || "Unavailable" }}
                          </div>
                        </div>
                        <div class="col-md-4 mb-2 text-center">
                          <div class="text-muted small">Location</div>
                          <div class="fw-bold text-dark">
                            {{ result.subjectLocation || "Unavailable" }}
                          </div>
                        </div>
                        <div class="col-md-4 mb-2 text-center">
                          <div class="text-muted small">Sentiment</div>
                          <div class="fw-bold text-dark">
                            {{
                              result.sentimentConfidenceLevel || "Unavailable"
                            }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="
                        (result.highlights && result.highlights.length) ||
                        result.url
                      "
                      class="pt-2"
                    >
                      <div
                        v-if="result.highlights && result.highlights.length"
                        class="mb-2"
                      >
                        <strong>Key Highlights:</strong>
                        <template v-if="result.highlights.length <= 3">
                          <div class="mb-0 mt-1 p-2 rounded border">
                            <ul>
                              <li
                                v-for="(highlight, hi) in result.highlights"
                                :key="hi"
                                class="small"
                              >
                                <span
                                  v-html="
                                    highlight.length > 500
                                      ? highlight.substring(0, 500) + '...'
                                      : highlight
                                  "
                                ></span>
                              </li>
                            </ul>
                          </div>
                        </template>
                        <template v-else>
                          <div class="mb-0 mt-1 p-2 rounded border">
                            <ul>
                              <li
                                v-for="(
                                  highlight, hi
                                ) in result.highlights.slice(0, 2)"
                                :key="hi"
                                class="small"
                              >
                                <span
                                  v-html="
                                    highlight.length > 500
                                      ? highlight.substring(0, 500) + '...'
                                      : highlight
                                  "
                                ></span>
                              </li>
                            </ul>
                            <span class="text-muted small d-block mt-1"
                              >+{{ result.highlights.length - 2 }} more
                              highlights</span
                            >
                          </div>
                        </template>
                      </div>
                      <div v-if="result.url" class="mt-2">
                        <strong>Source URL:</strong>
                        <a
                          :href="result.url"
                          target="_blank"
                          class="text-decoration-none small d-block mt-1"
                        >
                          {{ result.url }}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div class="report-meta">
              <div>
                <span class="text-muted">Ref:</span> {{ report_reference }}
              </div>
              <div>
                <span class="text-muted">Score Date:</span> {{ score_date }}
              </div>
              <div>
                <span class="text-muted">Generated:</span> {{ generated_date }}
              </div>
            </div>
          </SectionSheet>
        </template>
      </template>

      <!-- Negative Media Results exceed limit message -->
      <template v-if="nm_exceeds_limit && nm_results && nm_results.length > 0">
        <SectionSheet>
          <div class="limit-exceeded-notice mb-4">
            <div class="limit-card nm-limit">
              <div class="limit-header">
                <div class="limit-icon">
                  <i class="fas fa-exclamation-triangle"></i>
                </div>
                <div class="limit-title">
                  <h6>Results Limit Reached</h6>
                  <span class="limit-subtitle">Negative Media Results</span>
                </div>
              </div>
              <div class="limit-content">
                <div class="limit-stats">
                  <div class="stat-item">
                    <span class="stat-label">Showing</span>
                    <span class="stat-value">{{
                      limited_nm_results.length
                    }}</span>
                  </div>
                  <div class="stat-divider">of</div>
                  <div class="stat-item">
                    <span class="stat-label">Total</span>
                    <span class="stat-value">{{ nm_count }}</span>
                  </div>
                </div>
                <div class="limit-message">
                  <i class="fas fa-info-circle"></i>
                  <span
                    >{{ nm_count - limited_nm_results.length }} additional
                    results available via export</span
                  >
                </div>
                <div class="limit-action">
                  <i class="fas fa-download"></i>
                  <span
                    >Use the Export feature above to download complete data in
                    CSV or Excel format</span
                  >
                </div>
              </div>
            </div>
          </div>

          <div class="report-meta">
            <div>
              <span class="text-muted">Ref:</span> {{ report_reference }}
            </div>
            <div>
              <span class="text-muted">Score Date:</span> {{ score_date }}
            </div>
            <div>
              <span class="text-muted">Generated:</span> {{ generated_date }}
            </div>
          </div>
        </SectionSheet>
      </template>

      <!-- Negative Media Results - No Data Available -->
      <template v-if="!nm_results || nm_results.length === 0">
        <SectionSheet>
          <div class="card shadow-sm border bg-white mb-4">
            <div class="card-header">
              <h6 class="mb-0">Negative Media Results</h6>
            </div>
            <div class="card-body text-center py-4">
              <div class="alert alert-info mb-0">
                <i class="fas fa-info-circle fa-2x text-info mb-3"></i>
                <h6 class="text-info">No Negative Media Records Found</h6>
                <p class="mb-0">
                  No adverse media coverage or negative publicity was found for
                  <strong>{{ subject_full_name || "the subject" }}</strong> in
                  our comprehensive negative media databases.
                </p>
              </div>
            </div>
          </div>

          <div class="report-meta">
            <div>
              <span class="text-muted">Ref:</span> {{ report_reference }}
            </div>
            <div>
              <span class="text-muted">Generated:</span> {{ generated_date }}
            </div>
          </div>
        </SectionSheet>
      </template>

      <!-- PEP Results -->
      <template v-if="pep_results && pep_results.length > 0">
        <template
          v-for="(page, pageIndex) in pep_result_pages"
          :key="`pep-page-${pageIndex}`"
        >
          <SectionSheet>
            <!-- Section Header for first page -->
            <template v-if="page.isFirst">
              <div class="card shadow-sm border p-2 bg-white mb-3 pep-card">
                <div class="card-header">
                  <h6 class="mb-0">Politically Exposed Person (PEP) Results</h6>
                </div>
                <div class="card-body">
                  <p class="text-muted mb-0">
                    The following results were found in PEP screening databases.
                  </p>
                </div>
              </div>
            </template>

            <template
              v-for="(result, resultIndex) in page.results"
              :key="`pep-result-${pageIndex}-${resultIndex}`"
            >
              <div class="card border border-left-accent pep-card mb-3">
                <div class="card-body p-3">
                  <div class="pep-result-card">
                    <div
                      class="d-flex justify-content-between align-items-start mb-2"
                    >
                      <div class="result-header">
                        <span
                          class="badge text-white"
                          style="background-color: #358826"
                          >Score: {{ result.score }}</span
                        >
                        <span
                          v-if="result.gender"
                          class="badge bg-dark text-white ms-2"
                          >Gender: {{ result.gender }}</span
                        >
                        <span
                          v-if="result.pepSearchType !== undefined"
                          class="badge bg-secondary ms-2"
                        >
                          Type: {{ result.pepSearchType }}
                        </span>
                      </div>
                      <small class="text-muted"
                        >Profile ID: {{ result.profileId }}</small
                      >
                    </div>
                    <div class="row mb-2">
                      <div class="col-md-12 rounded p-2 bg-light">
                        <div class="row g-3">
                          <div class="col-md-4 mb-2 text-start">
                            <div class="text-muted small">Name</div>
                            <div class="fw-bold text-dark">
                              {{ result.name }}
                            </div>
                          </div>
                          <div class="col-md-4 mb-2 text-center">
                            <div class="text-muted small">Birth Name</div>
                            <div class="fw-bold text-dark">
                              {{ result.birthName || "Unavailable" }}
                            </div>
                          </div>
                          <div class="col-md-4 mb-2 text-end">
                            <div class="text-muted small">Country</div>
                            <div class="fw-bold text-dark">
                              {{ result.country }}
                            </div>
                          </div>
                          <div class="col-md-4 mb-2 text-start">
                            <div class="text-muted small">Date of Birth</div>
                            <div class="fw-bold text-dark">
                                {{
                                  result.dateOfBirth
                                    ? new Date(result.dateOfBirth).toLocaleDateString()
                                    : "Unavailable"
                                }}
                            </div>
                          </div>
                          <div class="col-md-4 mb-2 text-center">
                            <div class="text-muted small">Date of Death</div>
                            <div class="fw-bold text-dark">
                              <span>
                                {{
                                  result.dateOfDeath
                                    ? new Date(result.dateOfDeath).toLocaleDateString()
                                    : "Unavailable"
                                }}
                              </span>
                            </div>
                          </div>
                          <div class="col-md-4 mb-2 text-end">
                            <div class="text-muted small">Place of Birth</div>
                            <div class="fw-bold text-dark">
                              {{ result.placeOfBirth || "Unavailable" }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-12">
                        <div v-if="result.professionalHistory?.length">
                          <strong>Professional History:</strong><br />
                          <div class="mt-1">
                            <span
                              v-for="(
                                position, index
                              ) in result.professionalHistory"
                              :key="index"
                              class="badge bg-secondary small text-white me-1 mb-1"
                            >
                              {{ position }}
                            </span>
                          </div>
                        </div>

                        <div v-if="result.aliases?.length" class="mt-1">
                          <strong>Known Aliases:</strong><br />
                          <div class="mt-1">
                            <span
                              v-for="(alias, index) in result.aliases"
                              :key="index"
                              class="badge bg-dark small text-white me-1 mb-1"
                            >
                              {{ alias }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="result.familyMembers?.length"
                      class="mt-2 rounded"
                    >
                      <strong>Family Members:</strong>
                      <div class="mt-2 d-flex flex-wrap">
                        <div
                          v-for="(member, mi) in result.familyMembers"
                          :key="mi"
                          class="me-2 mb-2 border rounded px-2 py-1 bg-white"
                        >
                          <div class="d-flex align-items-center">
                            <i
                              class="bi bi-person-fill me-2 text-secondary"
                            ></i>
                            <span class="badge bg-secondary">{{
                              member.name
                            }}</span>
                            <span class="ms-1 text-muted small"
                              >({{ member.relationType }})</span
                            >
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="result.scoreBoosts?.length" class="mt-2">
                      <strong>Score Details:</strong>
                      <p class="text-muted small mb-0 fst-italic">
                        {{ result.scoreBoosts[0] }}
                      </p>
                    </div>
                    <div
                      v-if="result.sourceInfoURL?.length"
                      class="mt-3 pt-2 border-top"
                    >
                      <strong>Sources:</strong>
                      <div class="bg-light p-2 rounded mt-1">
                        <div
                          v-for="(url, ui) in result.sourceInfoURL.slice(0, 2)"
                          :key="ui"
                          class="small text-truncate"
                        >
                          <a
                            :href="url"
                            target="_blank"
                            class="text-decoration-none"
                          >
                            {{ url }}
                          </a>
                        </div>
                        <div
                          v-if="result.sourceInfoURL.length > 2"
                          class="text-muted small fst-italic"
                        >
                          +{{ result.sourceInfoURL.length - 2 }} more sources
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div class="report-meta">
              <div>
                <span class="text-muted">Ref:</span> {{ report_reference }}
              </div>
              <div>
                <span class="text-muted">Score Date:</span> {{ score_date }}
              </div>
              <div>
                <span class="text-muted">Generated:</span> {{ generated_date }}
              </div>
            </div>
          </SectionSheet>
        </template>
      </template>

      <!-- PEP Results exceed limit message -->
      <template
        v-if="pep_exceeds_limit && pep_results && pep_results.length > 0"
      >
        <SectionSheet>
          <div class="limit-exceeded-notice mb-4">
            <div class="limit-card pep-limit">
              <div class="limit-header">
                <div class="limit-icon">
                  <i class="fas fa-user-tie"></i>
                </div>
                <div class="limit-title">
                  <h6>Results Limit Reached</h6>
                  <span class="limit-subtitle"
                    >Politically Exposed Persons</span
                  >
                </div>
              </div>
              <div class="limit-content">
                <div class="limit-stats">
                  <div class="stat-item">
                    <span class="stat-label">Showing</span>
                    <span class="stat-value">{{
                      limited_pep_results.length
                    }}</span>
                  </div>
                  <div class="stat-divider">of</div>
                  <div class="stat-item">
                    <span class="stat-label">Total</span>
                    <span class="stat-value">{{ pep_count }}</span>
                  </div>
                </div>
                <div class="limit-message">
                  <i class="fas fa-info-circle"></i>
                  <span
                    >{{ pep_count - limited_pep_results.length }} additional
                    results available via export</span
                  >
                </div>
                <div class="limit-action">
                  <i class="fas fa-download"></i>
                  <span
                    >Use the Export feature above to download complete data in
                    CSV or Excel format</span
                  >
                </div>
              </div>
            </div>
          </div>

          <div class="report-meta">
            <div>
              <span class="text-muted">Ref:</span> {{ report_reference }}
            </div>
            <div>
              <span class="text-muted">Score Date:</span> {{ score_date }}
            </div>
            <div>
              <span class="text-muted">Generated:</span> {{ generated_date }}
            </div>
          </div>
        </SectionSheet>
      </template>

      <!-- PEP Results - No Data Available -->
      <template v-if="!pep_results || pep_results.length === 0">
        <SectionSheet>
          <div class="card shadow-sm border bg-white mb-4">
            <div class="card-header">
              <h6 class="mb-0">Politically Exposed Person (PEP) Results</h6>
            </div>
            <div class="card-body text-center py-4">
              <div class="alert alert-info mb-0">
                <i class="fas fa-user-tie fa-2x text-info mb-3"></i>
                <h6 class="text-info">No PEP Records Found</h6>
                <p class="mb-0">
                  <strong>{{ subject_full_name || "The subject" }}</strong> was
                  not found in any Politically Exposed Person databases. No
                  matches were identified for individuals holding prominent
                  political positions or their associates.
                </p>
              </div>
            </div>
          </div>

          <div class="report-meta">
            <div>
              <span class="text-muted">Ref:</span> {{ report_reference }}
            </div>
            <div>
              <span class="text-muted">Generated:</span> {{ generated_date }}
            </div>
          </div>
        </SectionSheet>
      </template>

      <!-- Watchlist Results -->
      <template v-if="wls_results && wls_results.length > 0">
        <template
          v-for="(chunk, chunk_index) in Math.ceil(
            limited_wls_results.length / 2
          )"
          :key="`wls-result-${chunk_index}`"
        >
          <SectionSheet>
            <!-- Section Header for first chunk -->
            <template v-if="chunk_index === 0">
              <div class="card shadow-sm border bg-white p-2 mb-3 wls-card">
                <div class="card-header">
                  <h6 class="mb-0">Watchlist Results</h6>
                </div>
                <div class="card-body">
                  <p class="text-muted mb-0">
                    The following results were found in sanctions and watchlist
                    databases.
                  </p>
                </div>
              </div>
            </template>
            <template
              v-for="(result, i) in limited_wls_results.slice(
                chunk_index === 0 ? 0 : chunk_index * 2 - 1,
                chunk_index === 0 ? 1 : chunk_index * 2 + 1
              )"
              :key="`wls-result-${
                chunk_index === 0 ? i : chunk_index * 2 - 1 + i
              }`"
            >
              <div class="card border border-left-accent wls-card mb-3">
                <div class="card-body p-3">
                  <div class="wls-result-card">
                    <div
                      class="d-flex justify-content-between align-items-start mb-2"
                    >
                      <div class="result-header">
                        <span
                          class="badge text-white"
                          style="background-color: #007313"
                          >Score: {{ result.score }}</span
                        >
                        <span class="badge bg-dark ms-2">
                          Category: {{ result.category }}</span
                        >
                      </div>
                      <small class="text-muted"
                        >ID: {{ result.productId }}</small
                      >
                    </div>
                    <div class="row mb-2 p-2">
                      <div class="col-md-12 rounded p-2 bg-light">
                        <div class="row">
                          <div class="col-md-4 mb-1 text-start">
                            <div class="text-muted small">Subject</div>
                            <div class="fw-bold text-dark">
                              {{ result.subjectMatched }}
                            </div>
                          </div>
                          <div class="col-md-4 mb-1 text-center">
                            <div class="text-muted small">Country</div>
                            <div class="fw-bold text-dark">
                              {{ result.sourceCountry }}
                            </div>
                          </div>
                          <div class="col-md-4 mb-1 text-end">
                            <div class="text-muted small">Source ID</div>
                            <div class="fw-bold text-dark">
                              {{ result.sourceId }}
                            </div>
                          </div>
                          <div class="col-md-4 mb-1 text-start">
                            <div class="text-muted small">Source Agency</div>
                            <div class="fw-bold text-dark">
                              {{ result.sourceAgencyName }}
                            </div>
                          </div>
                          <div class="col-md-4 mb-1 text-center">
                            <div class="text-muted small">Region</div>
                            <div class="fw-bold text-dark">
                              {{ result.sourceRegion }}
                            </div>
                          </div>
                          <div class="col-md-4 mb-1 text-end">
                            <div class="text-muted small">Agency</div>
                            <div class="fw-bold text-dark">
                              {{ result.sourceAgencyAcronym }}
                            </div>
                          </div>
                          <div class="col-md-4 mb-1 text-start">
                            <div class="text-muted small">List Type</div>
                            <div class="fw-bold text-dark">
                              {{ result.sourceListType }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="result.text" class="mt-2 mb-2">
                      <strong>Content:</strong>
                      <p class="text-muted small bg-light p-2 rounded mt-1">
                        {{
                          result.text.length > 250
                            ? result.text.substring(0, 250) + "..."
                            : result.text
                        }}
                      </p>
                    </div>

                    <div v-if="result.remarks" class="mt-2">
                      <strong>Remarks:</strong>
                      <p class="text-muted small mb-0 fst-italic">
                        {{ result.remarks }}
                      </p>
                    </div>
                    <div
                      v-if="
                        (result.highlights && result.highlights.length) ||
                        result.url
                      "
                      class="mt-3 pt-2 border-top"
                    >
                      <div v-if="result.highlights && result.highlights.length">
                        <strong>Key Highlights:</strong>
                        <template v-if="result.highlights.length <= 3">
                          <div class="mb-0 p-2 rounded border">
                            <ul>
                              <li
                                v-for="(highlight, hi) in result.highlights"
                                :key="hi"
                                class="small"
                              >
                                <span
                                  v-html="
                                    highlight.length > 500
                                      ? highlight.substring(0, 500) + '...'
                                      : highlight
                                  "
                                ></span>
                              </li>
                            </ul>
                          </div>
                        </template>
                        <template v-else>
                          <div class="mb-0 p-2 rounded border">
                            <ul>
                              <li
                                v-for="(
                                  highlight, hi
                                ) in result.highlights.slice(0, 2)"
                                :key="hi"
                                class="small"
                              >
                                <span
                                  v-html="
                                    highlight.length > 500
                                      ? highlight.substring(0, 500) + '...'
                                      : highlight
                                  "
                                ></span>
                              </li>
                            </ul>
                            <span class="text-muted small d-block mt-1"
                              >+{{ result.highlights.length - 2 }} more
                              highlights</span
                            >
                          </div>
                        </template>
                      </div>
                      <div v-if="result.url" class="mt-2">
                        <strong>Source URL:</strong>
                        <a
                          :href="result.url"
                          target="_blank"
                          class="text-decoration-none small d-block mt-1 text-truncate"
                        >
                          {{ result.url }}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div class="report-meta">
              <div>
                <span class="text-muted">Ref:</span> {{ report_reference }}
              </div>
              <div>
                <span class="text-muted">Score Date:</span> {{ score_date }}
              </div>
              <div>
                <span class="text-muted">Generated:</span> {{ generated_date }}
              </div>
            </div>
          </SectionSheet>
        </template>
      </template>

      <!-- Watchlist Results exceed limit message -->
      <template
        v-if="wls_exceeds_limit && wls_results && wls_results.length > 0"
      >
        <SectionSheet>
          <div class="limit-exceeded-notice mb-4">
            <div class="limit-card watchlist-limit">
              <div class="limit-header">
                <div class="limit-icon">
                  <i class="fas fa-list-alt"></i>
                </div>
                <div class="limit-title">
                  <h6>Results Limit Reached</h6>
                  <span class="limit-subtitle">Watchlist Results</span>
                </div>
              </div>
              <div class="limit-content">
                <div class="limit-stats">
                  <div class="stat-item">
                    <span class="stat-label">Showing</span>
                    <span class="stat-value">{{
                      limited_wls_results.length
                    }}</span>
                  </div>
                  <div class="stat-divider">of</div>
                  <div class="stat-item">
                    <span class="stat-label">Total</span>
                    <span class="stat-value">{{ wls_count }}</span>
                  </div>
                </div>
                <div class="limit-message">
                  <i class="fas fa-info-circle"></i>
                  <span
                    >{{ wls_count - limited_wls_results.length }} additional
                    results available via export</span
                  >
                </div>
                <div class="limit-action">
                  <i class="fas fa-download"></i>
                  <span
                    >Use the Export feature above to download complete data in
                    CSV or Excel format</span
                  >
                </div>
              </div>
            </div>
          </div>

          <div class="report-meta">
            <div>
              <span class="text-muted">Ref:</span> {{ report_reference }}
            </div>
            <div>
              <span class="text-muted">Score Date:</span> {{ score_date }}
            </div>
            <div>
              <span class="text-muted">Generated:</span> {{ generated_date }}
            </div>
          </div>
        </SectionSheet>
      </template>

      <!-- Watchlist Results - No Data Available -->
      <template v-if="!wls_results || wls_results.length === 0">
        <SectionSheet>
          <div class="card shadow-sm border bg-white mb-4">
            <div class="card-header">
              <h6 class="mb-0">Watchlist Results</h6>
            </div>
            <div class="card-body text-center py-4">
              <div class="alert alert-info mb-0">
                <i class="fas fa-shield-alt fa-2x text-info mb-3"></i>
                <h6 class="text-info">No Watchlist Records Found</h6>
                <p class="mb-0">
                  <strong>{{ subject_full_name || "The subject" }}</strong> was
                  not found on any local or international watchlists or
                  sanctions databases, including but not limited to the OFAC
                  (Office of Foreign Assets Control), United Nations Security
                  Council (UNSC) sanctions lists, or any applicable Targeted
                  Financial Sanctions (TFS) lists.
                </p>
              </div>
            </div>
          </div>

          <div class="report-meta">
            <div>
              <span class="text-muted">Ref:</span> {{ report_reference }}
            </div>
            <div>
              <span class="text-muted">Generated:</span> {{ generated_date }}
            </div>
          </div>
        </SectionSheet>
      </template>

      <SectionSheet>
        <div class="card shadow-sm border bg-white mb-4 p-2">
          <div class="card-header">
            <h6 class="mb-0">Disclaimer & Important Information</h6>
          </div>
          <div class="card-body">
            <p class="text-xs mb-2">
              <strong>External Data Source:</strong> This report contains data
              provided by an external service that has not been modified or
              changed in any way by
              <a
                href="https://www.goodpeopledata.com/"
                target="_blank"
                rel="noopener"
              >
                <strong>THE GOOD PEOPLE DATA COMPANY (PTY) LTD.</strong>
              </a>
              The accuracy, completeness, and timeliness of the information is
              dependent on the external service provider.
            </p>
            <p class="text-xs mb-2">
              <strong>Fuzzy Search Technology:</strong> This screening service
              utilizes fuzzy search technology that may produce inaccurate
              results and/or false positives. All potential matches require
              thorough human review and verification to determine if they
              represent true positive matches.
            </p>
            <p class="text-xs mb-0">
              This report is for informational purposes only and should not be
              the sole basis for any compliance or business decisions. The
              absence of matches does not guarantee that the subject is not
              involved in illicit activities, and the presence of matches does
              not confirm any wrongdoing.
            </p>
          </div>
          <div class="provider-box my-3 text-center">
            AML Service report was provided by
            <a
              href="https://www.goodpeopledata.com/"
              target="_blank"
              rel="noopener"
            >
              <strong>THE GOOD PEOPLE DATA COMPANY (PTY) LTD.</strong>
            </a>
          </div>
        </div>

        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Score Date:</span> {{ score_date }}
          </div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>
    </PageComponent>
  </div>
</template>
<script>
import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";

export default {
  name: "AMLReport",
  components: {
    PageControl,
    PageComponent,
    SectionSheet,
  },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    aml_data: {
      type: Object,
      default: () => {},
      required: false,
    },
    response_info: {
      type: Object,
      default: () => {},
      required: false,
    },
    date_scored: {
      type: String,
      default: "",
      required: false,
    },
  },
  data() {
    return {
      report_reference: "AML-" + (this.response_info?.ApplicationId || "N/A"),
      score_date: this.date_scored
        ? new Date(this.date_scored).toLocaleDateString("en-ZA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        : "",
      generated_date: new Date().toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
      nm_results: [],
      pep_results: [],
      wls_results: [],
      request_data: {},
      export_options: {
        subject_info: false,
        negative_media: false,
        pep_results: false,
        watchlist: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    total_matches() {
      return (
        (this.nm_results?.length || 0) +
        (this.pep_results?.length || 0) +
        (this.wls_results?.length || 0)
      );
    }, // Limited results for display (max 10 per type)
    limited_nm_results() {
      return this.nm_results?.slice(0, 11) || [];
    },
    limited_pep_results() {
      return this.pep_results?.slice(0, 11) || [];
    },
    limited_wls_results() {
      return this.wls_results?.slice(0, 11) || [];
    },
    // Check if results exceed the limit
    nm_exceeds_limit() {
      return this.nm_results?.length > 11;
    },
    pep_exceeds_limit() {
      return this.pep_results?.length > 11;
    },
    wls_exceeds_limit() {
      return this.wls_results?.length > 11;
    },
    pep_result_needs_own_page() {
      return (result) => {
        const max_items = 5; // Adjust this threshold as needed
        const prof_history_count = result.professionalHistory?.length || 0;
        const aliases_count = result.aliases?.length || 0;
        const family_members_count = result.familyMembers?.length || 0;

        return (
          prof_history_count >= max_items &&
          aliases_count >= max_items &&
          family_members_count >= max_items
        );
      };
    },
    pep_result_pages() {
      if (!this.limited_pep_results || this.limited_pep_results.length === 0)
        return [];

      const pages = [];
      let current_page = [];

      this.limited_pep_results.forEach((result, index) => {
        const needs_own_page = this.pep_result_needs_own_page(result);

        if (index === 0) {
          pages.push({
            isFirst: true,
            results: [result],
          });
        } else if (needs_own_page) {
          if (current_page.length > 0) {
            pages.push({
              isFirst: false,
              results: [...current_page],
            });
            current_page = [];
          }
          pages.push({
            isFirst: false,
            results: [result],
          });
        } else {
          current_page.push(result);
          if (
            current_page.length === 2 ||
            index === this.limited_pep_results.length - 1
          ) {
            pages.push({
              isFirst: false,
              results: [...current_page],
            });
            current_page = [];
          }
        }
      });

      return pages;
    },
    subject_full_name() {
      // First try to get from RequestData if available
      if (this.request_data) {
        const firstName = this.request_data.FirstName || "";
        const lastName = this.request_data.LastName || "";
        if (firstName || lastName) {
          return `${firstName} ${lastName}`.trim();
        }
      }

      // Fallback to existing logic
      if (this.pep_results?.length > 0) {
        return this.pep_results[0].name;
      } else if (this.nm_results?.length > 0) {
        return this.nm_results[0].subjectMatched;
      } else if (this.wls_results?.length > 0) {
        return this.wls_results[0].subjectMatched;
      }
      return "";
    },
    subject_first_name() {
      // First try to get from RequestData if available
      if (this.request_data?.FirstName) {
        return this.request_data.FirstName;
      }

      // Fallback to existing logic
      if (this.pep_results?.length > 0 && this.pep_results[0].firstName) {
        return this.pep_results[0].firstName;
      } else if (this.nm_results?.length > 0 && this.nm_results[0].firstName) {
        return this.nm_results[0].firstName;
      } else if (
        this.wls_results?.length > 0 &&
        this.wls_results[0].firstName
      ) {
        return this.wls_results[0].firstName;
      } else if (this.subject_full_name) {
        const name_parts = this.subject_full_name.split(" ");
        return name_parts[0] || "";
      }
      return "";
    },
    subject_last_name() {
      // First try to get from RequestData if available
      if (this.request_data?.LastName) {
        return this.request_data.LastName;
      }

      // Fallback to existing logic
      if (this.pep_results?.length > 0 && this.pep_results[0].lastName) {
        return this.pep_results[0].lastName;
      } else if (this.nm_results?.length > 0 && this.nm_results[0].lastName) {
        return this.nm_results[0].lastName;
      } else if (this.wls_results?.length > 0 && this.wls_results[0].lastName) {
        return this.wls_results[0].lastName;
      } else if (this.subject_full_name) {
        const name_parts = this.subject_full_name.split(" ");
        return name_parts.slice(1).join(" ") || "";
      }
      return "";
    },
    subject_date_of_birth() {
      // First try to get from RequestData if available
      if (this.request_data?.DateOfDirth) {
        const dob = this.request_data.DateOfDirth;
        // Handle YYYYMMDD format like in test.json
        if (dob && dob.length === 8) {
          const year = dob.substring(0, 4);
          const month = dob.substring(4, 6);
          const day = dob.substring(6, 8);
          const formatted_date = new Date(
            `${year}-${month}-${day}`
          ).toLocaleDateString("en-ZA");
          return formatted_date;
        }
      }

      // Fallback to existing logic
      if (this.pep_results?.length > 0 && this.pep_results[0].dateOfBirth) {
        return new Date(this.pep_results[0].dateOfBirth).toLocaleDateString(
          "en-ZA"
        );
      } else if (
        this.nm_results?.length > 0 &&
        this.nm_results[0].dateOfBirth
      ) {
        return new Date(this.nm_results[0].dateOfBirth).toLocaleDateString(
          "en-ZA"
        );
      } else if (
        this.wls_results?.length > 0 &&
        this.wls_results[0].dateOfBirth
      ) {
        return new Date(this.wls_results[0].dateOfBirth).toLocaleDateString(
          "en-ZA"
        );
      }
      return "";
    },
    subject_gender() {
      // First try to get from RequestData if available
      if (this.request_data?.Gender) {
        const gender = this.request_data.Gender;
        const formattedGender =
          gender === "M" ? "Male" : gender === "F" ? "Female" : gender;
        return formattedGender;
      }

      // Fallback to existing logic
      if (this.pep_results?.length > 0 && this.pep_results[0].gender) {
        return this.pep_results[0].gender;
      } else if (this.nm_results?.length > 0 && this.nm_results[0].gender) {
        return this.nm_results[0].gender;
      } else if (this.wls_results?.length > 0 && this.wls_results[0].gender) {
        return this.wls_results[0].gender;
      }
      return "";
    },
    subject_country() {
      // First try to get from RequestData if available
      if (this.request_data?.CountryCode) {
        const countryCode = this.request_data.CountryCode;
        // Convert common country codes to readable names
        const countryMap = {
          ZAF: "South Africa",
          USA: "United States",
          GBR: "United Kingdom",
          CAN: "Canada",
          AUS: "Australia",
          AFG: "Afghanistan",
          ALB: "Albania",
          DZA: "Algeria",
          AND: "Andorra",
          AGO: "Angola",
          ARG: "Argentina",
          ARM: "Armenia",
          AUT: "Austria",
          AZE: "Azerbaijan",
          BHS: "Bahamas",
          BHR: "Bahrain",
          BGD: "Bangladesh",
          BRB: "Barbados",
          BLR: "Belarus",
          BEL: "Belgium",
          BLZ: "Belize",
          BEN: "Benin",
          BTN: "Bhutan",
          BOL: "Bolivia",
          BIH: "Bosnia and Herzegovina",
          BWA: "Botswana",
          BRA: "Brazil",
          BRN: "Brunei",
          BGR: "Bulgaria",
          BFA: "Burkina Faso",
          BDI: "Burundi",
          KHM: "Cambodia",
          CMR: "Cameroon",
          CPV: "Cape Verde",
          CAF: "Central African Republic",
          TCD: "Chad",
          CHL: "Chile",
          CHN: "China",
          COL: "Colombia",
          COM: "Comoros",
          COG: "Congo",
          COD: "Congo (Democratic Republic)",
          CRI: "Costa Rica",
          CIV: "Côte d'Ivoire",
          HRV: "Croatia",
          CUB: "Cuba",
          CYP: "Cyprus",
          CZE: "Czech Republic",
          DNK: "Denmark",
          DJI: "Djibouti",
          DMA: "Dominica",
          DOM: "Dominican Republic",
          ECU: "Ecuador",
          EGY: "Egypt",
          SLV: "El Salvador",
          GNQ: "Equatorial Guinea",
          ERI: "Eritrea",
          EST: "Estonia",
          SWZ: "Eswatini",
          ETH: "Ethiopia",
          FJI: "Fiji",
          FIN: "Finland",
          FRA: "France",
          GAB: "Gabon",
          GMB: "Gambia",
          GEO: "Georgia",
          DEU: "Germany",
          GHA: "Ghana",
          GRC: "Greece",
          GRD: "Grenada",
          GTM: "Guatemala",
          GIN: "Guinea",
          GNB: "Guinea-Bissau",
          GUY: "Guyana",
          HTI: "Haiti",
          HND: "Honduras",
          HUN: "Hungary",
          ISL: "Iceland",
          IND: "India",
          IDN: "Indonesia",
          IRN: "Iran",
          IRQ: "Iraq",
          IRL: "Ireland",
          ISR: "Israel",
          ITA: "Italy",
          JAM: "Jamaica",
          JPN: "Japan",
          JOR: "Jordan",
          KAZ: "Kazakhstan",
          KEN: "Kenya",
          KIR: "Kiribati",
          PRK: "North Korea",
          KOR: "South Korea",
          KWT: "Kuwait",
          KGZ: "Kyrgyzstan",
          LAO: "Laos",
          LVA: "Latvia",
          LBN: "Lebanon",
          LSO: "Lesotho",
          LBR: "Liberia",
          LBY: "Libya",
          LIE: "Liechtenstein",
          LTU: "Lithuania",
          LUX: "Luxembourg",
          MDG: "Madagascar",
          MWI: "Malawi",
          MYS: "Malaysia",
          MDV: "Maldives",
          MLI: "Mali",
          MLT: "Malta",
          MHL: "Marshall Islands",
          MRT: "Mauritania",
          MUS: "Mauritius",
          MEX: "Mexico",
          FSM: "Micronesia",
          MDA: "Moldova",
          MCO: "Monaco",
          MNG: "Mongolia",
          MNE: "Montenegro",
          MAR: "Morocco",
          MOZ: "Mozambique",
          MMR: "Myanmar",
          NAM: "Namibia",
          NRU: "Nauru",
          NPL: "Nepal",
          NLD: "Netherlands",
          NZL: "New Zealand",
          NIC: "Nicaragua",
          NER: "Niger",
          NGA: "Nigeria",
          MKD: "North Macedonia",
          NOR: "Norway",
          OMN: "Oman",
          PAK: "Pakistan",
          PLW: "Palau",
          PSE: "Palestine",
          PAN: "Panama",
          PNG: "Papua New Guinea",
          PRY: "Paraguay",
          PER: "Peru",
          PHL: "Philippines",
          POL: "Poland",
          PRT: "Portugal",
          QAT: "Qatar",
          ROU: "Romania",
          RUS: "Russia",
          RWA: "Rwanda",
          KNA: "Saint Kitts and Nevis",
          LCA: "Saint Lucia",
          VCT: "Saint Vincent and the Grenadines",
          WSM: "Samoa",
          SMR: "San Marino",
          STP: "Sao Tome and Principe",
          SAU: "Saudi Arabia",
          SEN: "Senegal",
          SRB: "Serbia",
          SYC: "Seychelles",
          SLE: "Sierra Leone",
          SGP: "Singapore",
          SVK: "Slovakia",
          SVN: "Slovenia",
          SLB: "Solomon Islands",
          SOM: "Somalia",
          ESP: "Spain",
          LKA: "Sri Lanka",
          SDN: "Sudan",
          SUR: "Suriname",
          SWE: "Sweden",
          CHE: "Switzerland",
          SYR: "Syria",
          TWN: "Taiwan",
          TJK: "Tajikistan",
          TZA: "Tanzania",
          THA: "Thailand",
          TLS: "Timor-Leste",
          TGO: "Togo",
          TON: "Tonga",
          TTO: "Trinidad and Tobago",
          TUN: "Tunisia",
          TUR: "Turkey",
          TKM: "Turkmenistan",
          TUV: "Tuvalu",
          UGA: "Uganda",
          UKR: "Ukraine",
          ARE: "United Arab Emirates",
          URY: "Uruguay",
          UZB: "Uzbekistan",
          VUT: "Vanuatu",
          VAT: "Vatican City",
          VEN: "Venezuela",
          VNM: "Vietnam",
          YEM: "Yemen",
          ZMB: "Zambia",
          ZWE: "Zimbabwe",
        };
        const countryName = countryMap[countryCode] || countryCode;
        return countryName;
      }

      // Fallback to existing logic
      if (this.pep_results?.length > 0) {
        return this.pep_results[0].country;
      } else if (this.nm_results?.length > 0) {
        return this.nm_results[0].sourceCountry;
      } else if (this.wls_results?.length > 0) {
        return this.wls_results[0].sourceCountry;
      }
      return "";
    },
    application_id() {
      return this.response_info?.ApplicationId || "";
    },
    nm_count() {
      return this.nm_results?.length || 0;
    },
    pep_count() {
      return this.pep_results?.length || 0;
    },
    wls_count() {
      return this.wls_results?.length || 0;
    },
    has_selected_tables() {
      return Object.values(this.export_options).some((selected) => selected);
    },
  },
  mounted() {
    this.request_data = this.aml_data.RequestData || {};
    this.nm_results = this.aml_data.NmResults || [];
    this.pep_results = this.aml_data.PepResults || [];
    this.wls_results = this.aml_data.WlsResults || [];
  },
  methods: {
    close_modal() {
      $("#export-modal-" + this.application_id).modal("hide");
    },
    show_export_modal() {
      this.export_options.subject_info = this.total_matches > 0;
      this.export_options.negative_media = this.nm_count > 0;
      this.export_options.pep_results = this.pep_count > 0;
      this.export_options.watchlist = this.wls_count > 0;

      $("#export-modal-" + this.application_id).modal("show");
    },
    async load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async ensure_libraries_loaded() {
      try {
        if (this.export_format === "csv" && !this.libraries_loaded.papa_parse) {
          if (typeof Papa === "undefined") {
            await this.load_script(
              "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js"
            );
          }
          this.libraries_loaded.papa_parse = true;
        }

        if (this.export_format === "excel" && !this.libraries_loaded.sheetjs) {
          if (typeof XLSX === "undefined") {
            await this.load_script(
              "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"
            );
          }
          this.libraries_loaded.sheetjs = true;
        }
      } catch (error) {
        throw new Error("Failed to load export libraries");
      }
    },
    strip_html(html_string) {
      if (!html_string) return "";
      const temp_div = document.createElement("div");
      temp_div.innerHTML = html_string;
      return temp_div.textContent || temp_div.innerText || "";
    },
    get_current_date_string() {
      const current_date = new Date();
      return current_date.toISOString().split("T")[0];
    },
    prepare_subject_data() {
      return [
        {
          "Full Name": this.subject_full_name || "N/A",
          "First Name": this.subject_first_name || "N/A",
          "Last Name": this.subject_last_name || "N/A",
          "Date of Birth": this.subject_date_of_birth || "N/A",
          Gender: this.subject_gender || "N/A",
          Country: this.subject_country || "N/A",
          "Application ID": this.application_id || "N/A",
          "Report Reference": this.report_reference,
          "Generated Date": this.generated_date,
        },
      ];
    },
    prepare_negative_media_data() {
      return this.nm_results.map((result) => {
        const clean_highlights = result.highlights
          ? result.highlights.map((h) => this.strip_html(h)).join(" | ")
          : "";

        return {
          "Product ID": result.productId || "",
          Score: result.score || "",
          Category: result.category || "",
          "Source Country": result.sourceCountry || "",
          "Source ID": result.sourceId || "",
          "Subject Matched": result.subjectMatched || "",
          "Subject Age": result.subjectAge || "",
          "Subject Location": result.subjectLocation || "",
          "Sentiment Confidence Level": result.sentimentConfidenceLevel || "",
          "Article Title": result.articleTitle || "",
          "Article Date": result.articleDate || "",
          Concepts: result.concepts ? result.concepts.join(", ") : "",
          Highlights: clean_highlights,
          "Source URL": result.url || "",
        };
      });
    },
    prepare_pep_data() {
      return this.pep_results.map((result) => {
        const professional_history = result.professionalHistory
          ? result.professionalHistory.join(" | ")
          : "";
        const aliases = result.aliases ? result.aliases.join(" | ") : "";
        const family_members = result.familyMembers
          ? result.familyMembers
              .map((member) => `${member.name} (${member.relationType})`)
              .join(" | ")
          : "";
        const source_urls = result.sourceInfoURL
          ? result.sourceInfoURL.join(" | ")
          : "";

        return {
          "Profile ID": result.profileId || "",
          Score: result.score || "",
          Name: result.name || "",
          "Birth Name": result.birthName || "",
          Country: result.country || "",
          "Date of Birth": result.dateOfBirth || "",
          "Date of Death": result.dateOfDeath || "",
          "Place of Birth": result.placeOfBirth || "",
          Gender: result.gender || "",
          "PEP Search Type": result.pepSearchType || "",
          "Professional History": professional_history,
          "Known Aliases": aliases,
          "Family Members": family_members,
          "Score Details": result.scoreBoosts ? result.scoreBoosts[0] : "",
          "Source URLs": source_urls,
        };
      });
    },
    prepare_watchlist_data() {
      return this.wls_results.map((result) => {
        const clean_highlights = result.highlights
          ? result.highlights.map((h) => this.strip_html(h)).join(" | ")
          : "";

        return {
          "Product ID": result.productId || "",
          Score: result.score || "",
          Category: result.category || "",
          "Subject Matched": result.subjectMatched || "",
          "Source Country": result.sourceCountry || "",
          "Source ID": result.sourceId || "",
          "Source Agency": result.sourceAgencyName || "",
          "Source Agency Acronym": result.sourceAgencyAcronym || "",
          "Source Region": result.sourceRegion || "",
          "Source List Type": result.sourceListType || "",
          Content: result.text || "",
          Remarks: result.remarks || "",
          Highlights: clean_highlights,
          "Source URL": result.url || "",
        };
      });
    },
    async export_csv() {
      const Papa = window.Papa;
      const current_date = this.get_current_date_string();
      const tables_to_export = [];

      if (this.export_options.subject_info) {
        const subject_data = this.prepare_subject_data();
        const csv_content = Papa.unparse(subject_data);
        tables_to_export.push({
          name: `AML_Report_SubjectInfo_${current_date}.csv`,
          content: csv_content,
        });
      }

      if (this.export_options.negative_media && this.nm_count > 0) {
        const nm_data = this.prepare_negative_media_data();
        const csv_content = Papa.unparse(nm_data);
        tables_to_export.push({
          name: `AML_Report_NegativeMedia_${current_date}.csv`,
          content: csv_content,
        });
      }

      if (this.export_options.pep_results && this.pep_count > 0) {
        const pep_data = this.prepare_pep_data();
        const csv_content = Papa.unparse(pep_data);
        tables_to_export.push({
          name: `AML_Report_PEP_${current_date}.csv`,
          content: csv_content,
        });
      }

      if (this.export_options.watchlist && this.wls_count > 0) {
        const wls_data = this.prepare_watchlist_data();
        const csv_content = Papa.unparse(wls_data);
        tables_to_export.push({
          name: `AML_Report_Watchlist_${current_date}.csv`,
          content: csv_content,
        });
      }

      tables_to_export.forEach((table) => {
        const blob = new Blob([table.content], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", table.name);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    },
    async export_excel() {
      const XLSX = window.XLSX;
      const current_date = this.get_current_date_string();
      const workbook = XLSX.utils.book_new();

      if (this.export_options.subject_info) {
        const subject_data = this.prepare_subject_data();
        const worksheet = XLSX.utils.json_to_sheet(subject_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Subject Info");
      }

      if (this.export_options.negative_media && this.nm_count > 0) {
        const nm_data = this.prepare_negative_media_data();
        const worksheet = XLSX.utils.json_to_sheet(nm_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Negative Media");
      }

      if (this.export_options.pep_results && this.pep_count > 0) {
        const pep_data = this.prepare_pep_data();
        const worksheet = XLSX.utils.json_to_sheet(pep_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "PEP Results");
      }

      if (this.export_options.watchlist && this.wls_count > 0) {
        const wls_data = this.prepare_watchlist_data();
        const worksheet = XLSX.utils.json_to_sheet(wls_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Watchlist");
      }
      const filename = `AML_Report_${current_date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },
    prepare_raw_json_data() {
      return {
        report_metadata: {
          report_reference: this.report_reference,
          application_id: this.application_id,
          score_date: this.score_date,
          generated_date: this.generated_date,
          export_timestamp: new Date().toISOString(),
        },

        aml_data: this.aml_data,
      };
    },
    async export_json() {
      const current_date = this.get_current_date_string();
      const export_data = {};

      if (this.export_options.subject_info) {
        export_data.subject_info = this.prepare_subject_data()[0];
      }

      if (this.export_options.negative_media && this.nm_count > 0) {
        export_data.negative_media_results = this.prepare_negative_media_data();
      }

      if (this.export_options.pep_results && this.pep_count > 0) {
        export_data.pep_results = this.prepare_pep_data();
      }

      if (this.export_options.watchlist && this.wls_count > 0) {
        export_data.watchlist_results = this.prepare_watchlist_data();
      }

      if (this.export_options.raw_json) {
        export_data.raw_data = this.prepare_raw_json_data();
      }

      const json_content = JSON.stringify(export_data, null, 2);
      const blob = new Blob([json_content], {
        type: "application/json;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `AML_Report_${current_date}.json`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async export_data() {
      try {
        await this.ensure_libraries_loaded();
        if (this.export_format === "csv") {
          await this.export_csv();
        } else if (this.export_format === "excel") {
          await this.export_excel();
        } else if (this.export_format === "json") {
          await this.export_json();
        }
        $("#export-modal-" + this.application_id).modal("hide");
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Export Failed",
          text: "There was a problem exporting your data. Please try again.",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    },
  },
};
</script>

<style scoped>
.sheet {
  padding: 10mm !important;
}

#aml-report {
  font-family: "Arial", sans-serif;
  font-size: 12px;
}

.card {
  border: none;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
  overflow: hidden;
}

.card-header {
  background: white !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-header h6 {
  margin: 0 0 0 -16px;
  padding: 0 0 0 20px;
  border-left: 3px solid #59ff74;
  font-weight: 600;
  line-height: 1.5;
  font-size: 1rem;
}

.card-body {
  padding: 1.25rem !important;
}

.report-meta {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
  padding: 10px 10px;
  border-top: 1px dashed #eaeaea;
  font-size: 10px;
  color: #6c757d;
}

/* Card styling for AML results */
.border-left-accent {
  border-left: 3px solid #59ff74 !important;
}

.nm-card.border-left-accent {
  border-left-color: #036016 !important;
}

.pep-card.border-left-accent {
  border-left-color: #358826 !important;
}

.wls-card.border-left-accent {
  border-left-color: #007313 !important;
}

.result-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Section specific styling */
.nm-result-card {
  border-radius: 6px;
  padding: 0.75rem;
  margin: -0.5rem;
}

.pep-result-card {
  border-radius: 6px;
  padding: 0.75rem;
  margin: -0.5rem;
}

.wls-result-card {
  border-radius: 6px;
  padding: 0.75rem;
  margin: -0.5rem;
}

/* Additional styling for enhanced content */
.badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  font-weight: 500;
  border-radius: 4px;
}

.card-body p {
  margin-bottom: 0.5rem;
}

.card-body ul {
  padding-left: 1.2rem;
}

.card-body ul li {
  margin-bottom: 0.25rem;
}

.border-top {
  border-top: 1px solid #dee2e6 !important;
}

/* Highlight emphasis styling */
em {
  background-color: #fff3cd;
  padding: 0.1rem 0.2rem;
  border-radius: 0.2rem;
  font-style: normal;
  font-weight: 600;
}

/* URL truncation for better layout */
.text-decoration-none {
  text-decoration: none !important;
  color: #0d6efd;
}

.text-decoration-none:hover {
  text-decoration: underline !important;
  color: #0a58ca;
}

/* Strong text styling */
strong {
  font-weight: 600;
  color: #495057;
}

.bi-newspaper {
  font-size: 1.2rem;
  color: #036016;
}

/* Limit exceeded notice styling - max 5 per type */
.limit-exceeded-notice {
  margin: 1.5rem 0;
}

.limit-card {
  border: 2px solid #036016;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(3, 96, 22, 0.15);
  overflow: hidden;
  position: relative;
}

.limit-card.nm-limit {
  border-color: #036016;
  box-shadow: 0 4px 12px rgba(3, 96, 22, 0.15);
}

.limit-card.pep-limit {
  border-color: #358826;
  box-shadow: 0 4px 12px rgba(16, 43, 22, 0.15);
}

.limit-card.watchlist-limit {
  border-color: #007313;
  box-shadow: 0 4px 12px rgba(0, 115, 19, 0.15);
}

.limit-header {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid rgba(3, 96, 22, 0.2);
}

.pep-limit .limit-header {
  border-bottom-color: rgba(16, 43, 22, 0.2);
}

.watchlist-limit .limit-header {
  border-bottom-color: rgba(0, 115, 19, 0.2);
}

.limit-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #036016;
  color: #fff;
  border-radius: 10px;
  margin-right: 0.75rem;
  font-size: 1.1rem;
}

.nm-limit .limit-icon {
  background: #036016;
}

.pep-limit .limit-icon {
  background: #358826;
}

.watchlist-limit .limit-icon {
  background: #007313;
}

.limit-title h6 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
}

.limit-subtitle {
  font-size: 0.85rem;
  color: #666;
  font-weight: 500;
}

.limit-content {
  padding: 1rem 1.25rem 1.25rem;
}

.limit-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 8px;
  backdrop-filter: blur(5px);
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-label {
  font-size: 0.75rem;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #036016;
  line-height: 1;
  margin-top: 0.25rem;
}

.pep-limit .stat-value {
  color: #358826;
}

.watchlist-limit .stat-value {
  color: #007313;
}

.stat-divider {
  font-size: 0.9rem;
  color: #999;
  font-weight: 500;
  margin: 0 0.5rem;
}

.limit-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: #555;
}

.limit-message i {
  color: #036016;
  font-size: 0.95rem;
}

.pep-limit .limit-message i {
  color: #358826;
}

.watchlist-limit .limit-message i {
  color: #007313;
}

.limit-action {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  font-size: 0.85rem;
  color: #444;
  font-weight: 500;
}

.limit-action i {
  color: #28a745;
  font-size: 0.9rem;
}

/* Legacy alert styles (kept for backward compatibility) */
.alert-info {
  background-color: #d1ecf1 !important;
  border-color: #bee5eb !important;
  color: #0c5460 !important;
  border-radius: 6px;
}

.fa-info-circle {
  opacity: 0.8;
}

/* Specification of Checks Performed Styles */
.check-item {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  height: 100%;
  transition: all 0.2s ease;
}

.check-item:hover {
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.check-header {
  color: #333;
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
}

.check-header i {
  width: 20px;
  text-align: center;
}

.check-status .badge {
  font-size: 0.75rem;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
}
</style>
